import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../config/firebase';

// ═══════════════════════════════════════════════════════════════
// ADMIN: Driver Recheck Dashboard (Batch 16 M2)
// ═══════════════════════════════════════════════════════════════

const RECHECK_ITEMS = [
  { key: 'criminalHistory', label: 'Criminal History Check' },
  { key: 'drivingRecord', label: 'Driving Record Check' },
  { key: 'medicalFitness', label: 'Medical Fitness Review' },
  { key: 'wwcc', label: 'WWCC re-verification', conditional: true },
  { key: 'policyAcknowledgement', label: 'Policy Acknowledgement' },
];

export default function AdminRecheck() {
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);

  const load = async () => {
    setLoading(true);
    const snap = await getDocs(query(
      collection(db, 'users'),
      where('role', '==', 'driver'),
      where('status', '==', 'active')
    ));
    const list = [];
    snap.forEach(d => list.push({ id: d.id, ...d.data() }));
    setDrivers(list);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const daysUntil = (date) => {
    if (!date) return null;
    const d = date.toDate ? date.toDate() : new Date(date);
    return Math.floor((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  };

  const fmtDate = (date) => {
    if (!date) return '—';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const classifyDriver = (driver) => {
    const days = daysUntil(driver.recheck?.nextDueDate);
    if (days === null) return 'unscheduled';
    if (days < 0) return 'overdue';
    if (days <= 30) return 'imminent';
    if (days <= 60) return 'upcoming';
    return 'current';
  };

  const filteredDrivers = drivers.filter(d => {
    const c = classifyDriver(d);
    if (filter === 'all') return c !== 'current';
    return c === filter;
  });

  const recordItem = async (driverId, itemKey, completed) => {
    const notes = completed ? prompt(`Notes for ${itemKey} (optional):`) : null;
    try {
      const callable = httpsCallable(functions, 'adminRecordRecheckItem');
      await callable({ driverId, itemKey, completed, notes: notes || null });
      await load();
    } catch (e) {
      alert(`Failed: ${e.message}`);
    }
  };

  const completeRecheck = async (driverId) => {
    if (!confirm('Complete the recheck and set next due date 12 months from today?')) return;
    try {
      const callable = httpsCallable(functions, 'adminCompleteRecheck');
      const result = await callable({ driverId });
      alert(`Recheck completed. Next due: ${new Date(result.data.nextDueDate).toLocaleDateString('en-AU')}`);
      await load();
    } catch (e) {
      alert(`Failed: ${e.message}`);
    }
  };

  if (loading) return <div className="p-10 text-center">Loading...</div>;

  return (
    <div className="max-w-5xl mx-auto p-6">
      <button onClick={() => navigate(-1)}
        className="bg-transparent border-0 text-brand-primary font-semibold text-[0.9rem] cursor-pointer p-0 mb-4">
        ← Back
      </button>

      <h1 className="font-serif text-3xl text-brand-dark mb-4">Driver Rechecks</h1>

      <div className="flex gap-2 mb-5 flex-wrap">
        {[
          { key: 'all', label: 'All non-current' },
          { key: 'overdue', label: 'Overdue' },
          { key: 'imminent', label: 'Due ≤30d' },
          { key: 'upcoming', label: 'Due ≤60d' },
          { key: 'unscheduled', label: 'Unscheduled' },
        ].map(t => (
          <button key={t.key} onClick={() => setFilter(t.key)}
            className={`px-3 py-1.5 rounded-full border-[1.5px] cursor-pointer font-semibold text-[0.85rem] ${
              filter === t.key ? 'border-brand-primary bg-brand-primary text-white' : 'border-slate-200 bg-white text-body'
            }`}>{t.label}</button>
        ))}
      </div>

      {filteredDrivers.length === 0 ? (
        <div className="p-10 text-center text-muted">
          No drivers in this category.
        </div>
      ) : (
        <div className="grid gap-3">
          {filteredDrivers.map(driver => {
            const days = daysUntil(driver.recheck?.nextDueDate);
            const cls = classifyDriver(driver);
            const palette = {
              overdue: { bg: '#fee2e2', color: '#dc2626' },
              imminent: { bg: '#fee2e2', color: '#dc2626' },
              upcoming: { bg: '#fef3c7', color: '#92400e' },
              unscheduled: { bg: '#f1f5f9', color: '#5a6578' },
              current: { bg: 'rgba(45,212,168,0.1)', color: '#059669' },
            }[cls];
            const expanded = expandedId === driver.id;

            return (
              <div key={driver.id} className="p-3.5 rounded-xl bg-white border border-slate-200">
                <div onClick={() => setExpandedId(expanded ? null : driver.id)}
                  className="cursor-pointer flex justify-between items-center">
                  <div>
                    <div className="font-bold text-brand-dark">
                      {driver.firstName} {driver.lastName} · {driver.driverRef || driver.id.slice(0, 6)}
                    </div>
                    <div className="text-[0.8rem] text-muted">
                      Due: {fmtDate(driver.recheck?.nextDueDate)} · {driver.email}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {driver.recheckBlocked && (
                      <span className="px-2.5 py-1 rounded-full text-[0.72rem] font-bold bg-red-100 text-red-600">BLOCKED</span>
                    )}
                    <span className="px-2.5 py-1 rounded-full text-[0.72rem] font-bold"
                      style={{ background: palette.bg, color: palette.color }}>
                      {days === null ? 'Unscheduled' : days < 0 ? `${Math.abs(days)}d overdue` : `${days}d`}
                    </span>
                    <span className="text-muted">{expanded ? '▲' : '▼'}</span>
                  </div>
                </div>

                {expanded && (
                  <div className="mt-3 pt-3 border-t border-slate-200">
                    {RECHECK_ITEMS.map(item => {
                      if (item.conditional && !driver.documents?.wwcc?.expiryDate) return null;
                      const itemData = driver.recheck?.items?.[item.key];
                      const done = !!itemData?.completed;
                      return (
                        <div key={item.key}
                          className="flex justify-between items-center py-2 border-b border-slate-100">
                          <div className="flex-1">
                            <div className="text-[0.88rem] font-semibold">{item.label}</div>
                            {done && (
                              <div className="text-[0.75rem] text-muted mt-0.5">
                                Completed {fmtDate(itemData.completedAt)}
                                {itemData.notes && ` · ${itemData.notes}`}
                              </div>
                            )}
                          </div>
                          <button onClick={() => recordItem(driver.id, item.key, !done)}
                            className={`px-2.5 py-1 rounded-md border-0 text-white cursor-pointer font-semibold text-[0.78rem] ${
                              done ? 'bg-slate-400' : 'bg-emerald-600'
                            }`}>
                            {done ? 'Mark incomplete' : 'Mark complete'}
                          </button>
                        </div>
                      );
                    })}
                    <button onClick={() => completeRecheck(driver.id)}
                      className="mt-3 w-full px-3.5 py-2.5 rounded-lg border-0 bg-brand-primary text-white cursor-pointer font-bold text-[0.88rem]">
                      Complete recheck (advances next due date 12 months)
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}